import { dialog } from "../../../nq-component";
import BaseListPresenter from "../../../base/BaseListPresenter";
import saveAs from "../../../saveAs";
import {
  deleteObjectUseCase,
  updateObjectUseCase,
} from "../../../usecases/object";
import { getCurrentUserUseCase } from "../../../usecases/user";
import { footer, html } from "framer-motion/m";
import DialogNote from "./components/DialogNote";

class UserListPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
  }

  componentDidMount() {
    const token = this.view.getToken();
    this.init();
    this.getUser(token);
    return this.getObjects();
  }

  init() {
    this.limit = 20;
    this.where = {
      "roles.id": { $ne: "Applicants" },
    };
    this.search = {};
    this.filter = {};
    this.include = ["employee"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  onClickAdd() {
    const collection = this.view.getCollectionName();
    this.view.navigateTo("/admin/employee/form");
  }

  async onClickEdit(empDetails) {
    const collection = this.view.getCollectionName();
    const query = {
      where: { id: empDetails.id },
      include: ["employee", "location"],
    };
    const response = await this.findObjectUseCase.execute(collection, query);

    if (response && response.length > 0) {
      const object = response[0];

      this.view.setState(
        {
          formData: {
            id: object?.id,
            employee: object?.employee || {},
            username: object?.username || "",
            password: "",
            window: object?.window || "",
            roles: object?.roles?.[0] || {},
            location: object?.location || {},
            clients: object?.clients || [],
          },
          showModal: true,
        },
        // () => {
        //   this.view.onClickAdd(object);
        // }
      );
    }
  }

  async onClickDelete(empDetails) {
    dialog.close();
    const object = empDetails;
    const collection = this.view.getCollectionName();

    await deleteObjectUseCase().execute(collection, object.id);
    this.view.setObjects(this.objects);
    this.view.setState({
      showDetails: false,
    });
    this.view.successModal("User successfully removed.");
    this.reset();
    this.getObjects();
  }

  isInputsValidated(formData) {
    console.log(formData);
    if (!formData?.employee?.id && formData?.roles.id !== "Applicants") {
      this.view.showNote("Employee details are required.");
      this.view.setState({ submitting: false });
      return false;
    }

    if (!formData.username || formData.username === "") {
      this.view.showNote("Username is required.");
      this.view.setState({ submitting: false });
      return false;
    }

    if (formData.username !== formData.username.toLowerCase()) {
      this.view.showNote("Username should be in lowercase.");
      this.view.setState({ submitting: false });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      !emailRegex.test(formData.username) &&
      formData?.roles.id !== "Applicants"
    ) {
      this.view.showNote("Username must be in an email-like format.");
      this.view.setState({ submitting: false });
      return false;
    }

    if (!formData?.roles || Object.keys(formData?.roles).length === 0) {
      this.view.showNote("Role is required.");
      this.view.setState({ submitting: false });
      return false;
    }

    if (formData.id === "" && !formData.password) {
      this.view.showNote("Password is required for new users.");
      this.view.setState({ submitting: false });
      return false;
    }

    return true;
  }

  async generateEmployeeNumber(role) {
    let query;

    if (role === "Employee") {
      query = {
        where: {
          "roles.id": { $in: ["Employee"] },
        },
      };
    } else {
      query = {
        where: {
          "roles.id": { $nin: ["Employee", "Applicant"] },
        },
      };
    }

    try {
      const employees = await this.findObjectUseCase.execute("users", query);
      const employeeLength = employees?.length || 0;

      console.log(employees);
      const nextNumber = employeeLength + 1;
      return String(nextNumber).padStart(2, "0");
    } catch (error) {
      console.error("Error", error);
      return "01";
    }
  }

  async handleSubmit(formData) {
    if (!this.isInputsValidated(formData)) {
      return false;
    }

    const collection = this.view.getCollectionName();
    const acl = this.view.getAcl();
    const isNew = !formData.id;

    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    let employeeId = "";
    const roleId = formData?.roles?.id;

    if (roleId === "Applicant") {
      employeeId = "Applicant";
    } else {
      const nextIncrement = await this.generateEmployeeNumber(roleId);

      employeeId =
        roleId === "Employee"
          ? `LGS${year}-${nextIncrement}`
          : `${year}${month}${day}${nextIncrement}`;
    }

    const sendDetails = {
      ...formData,
      roles: [formData.roles],
      employeeId,
      employee: formData?.employee?.id
        ? { id: formData.employee.id }
        : undefined,
      location: formData?.location?.id
        ? { id: formData.location.id }
        : undefined,
      password: formData?.password || undefined,
      acl,
    };

    if (!formData.id) {
      delete sendDetails.id;
    }

    try {
      await this.upsertUseCase.execute(collection, sendDetails);
      this.view.setObjects(this.objects);
      this.view.closeModal();
      this.view.successModal(
        isNew ? "User successfully added." : "User successfully updated.",
      );
      this.reset();
      this.getObjects();
      this.view.setState({ showDetails: false });
      return true;
    } catch (error) {
      console.error("Error", error);
      this.view.showError(error);
      this.view.setState({ submitting: false });
      return false;
    }
  }

  onChangePassword(value, field) {
    this.password[field] = value;
  }

  async getUser(token) {
    const options = { session: token };

    try {
      const user = await getCurrentUserUseCase().execute(options);
      if (user && typeof user === "object") {
        this.user = user;
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      this.view.navigateTo("/expired");
    }
  }

  validatePassword(newPassword, confirmNewPassword) {
    console.log("newPassword", newPassword);
    console.log("confirmNewPassword", confirmNewPassword);
    if (newPassword !== confirmNewPassword) {
      return "Password must be the same.";
    }

    // if (newPassword.length < 8 || newPassword.length > 16) {
    //   return "Password length must be 8-16 characters.";
    // }

    const validation =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&])(?=.*\d)[A-Za-z\d@$!%*?&]+$/;

    // if (!validation.test(newPassword)) {
    //   return "Password must contain at least one uppercase letter, one lowercase letter, one special character, and one number.";
    // }

    return null;
  }

  async onSubmitPassword(password, userObject) {
    const collection = "users";
    const user = this.user;
    const { newPassword, confirmNewPassword } = password;
    const { ...updatedObject } = userObject;
    updatedObject.employee = updatedObject?.employee?.id
      ? { id: updatedObject.employee.id }
      : undefined;
    updatedObject.location = updatedObject?.location?.id
      ? { id: updatedObject.location.id }
      : undefined;
    updatedObject.password = newPassword;

    const errorMessage = this.validatePassword(newPassword, confirmNewPassword);

    if (errorMessage) {
      this.view.showNote(errorMessage);
      return;
    }

    try {
      const object = await this.upsertUseCase.execute(
        collection,
        updatedObject,
      );
      dialog.close();
      this.objects = this.objects.map((obj) =>
        obj.id === updatedObject.id ? object : obj,
      );
      this.view.setState({ showDetails: false, empDetails: {} });
      this.view.setObjects(this.objects);
      this.view.successModal("Successfully Updated!");
    } catch (error) {
      this.view.showError(error);
    }

    // user.password = newPassword;
    // this.updateUser(user);
  }

  updateUser(user) {
    this.view.showProgress();
    delete user.isMaster;

    updateObjectUseCase()
      .execute("users", user)
      .then((user) => {
        this.view.hideProgress();
        this.user = user;
        this.view.showSuccess("Successfully saved!");
      })
      .catch((error) => {
        this.view.hideProgress();
        this.view.showError(error);
      });
  }
}

export default UserListPresenter;
